import * as Mui from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CustomLoader from "../../../util/component/CustomLoader";
import ParkingToolService from "./ParkingToolService";
import ParkingToolSpace from "./ParkingToolTypes";

type Props = {
  form: UseFormReturn<ParkingToolSpace.FormInputs>;
  onSubmit: (data: ParkingToolSpace.FormInputs) => void;
}

const ParkingToolFilter = (props: Props) => {
  const { form, onSubmit } = props;

  const { t } = useTranslation([ "network" ]);

  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [tenantOptions, setTenantOptions] = useState<string[]>([]);
  const [locationOptions, setLocationOptions] = useState<any[]>([]);

  const getTenants = () => {
    const user: any = JSON.parse(localStorage.getItem("user") || "");
    setTenantOptions(user?.legalEntities?.filter((element: any) => element.name !== "all").map((element: any) => element.name));
  }

  const getLocations = () => {
    const tenant = form.watch("tenant");
    if (!tenant) return;

    setLoadingLocations(true);
    ParkingToolService.get.locations(tenant, t).then(
      (response) => {
        setLoadingLocations(false);
        setLocationOptions(response);
        if (response.length === 1) {
          form.setValue("location", response[0]);
          form.handleSubmit(onSubmit)();
        }
      }
    )
  }

  useEffect(() => {
    getTenants();
  }, []);

  useEffect(() => {
    getLocations();
  }, [form.watch("tenant")]);

  useEffect(() => {
    if (tenantOptions && tenantOptions.length > 0) {
      form.setValue("tenant", tenantOptions[0]);
    }
  }, [tenantOptions]);

  const onReset = () => {
    form.reset({
      tenant: tenantOptions[0],
    });
  }

  const loading = useMemo(() => {
    return loadingLocations || loadingTenants || false;
  }, [loadingLocations, loadingTenants]);

  return (
    <form id="parking-tool-filter" onSubmit={form.handleSubmit(onSubmit)}>
      <Mui.Card>
        <Mui.CardContent>
          <Mui.Grid container spacing={2.5} alignItems="center">
            {tenantOptions.length > 1 && (
              <Mui.Grid item xs={12} sm={6} md={4} lg={3}>
                <Mui.Autocomplete
                  options={tenantOptions}
                  value={form.watch("tenant") || ""}
                  onChange={(event, value) => form.setValue("tenant", value)}
                  renderInput={(params) => (
                    <Mui.TextField
                      {...params}
                      label={t("parkingTool.tenant", "tenant")}
                    />
                  )}
                />
              </Mui.Grid>
            )}
            
            <Mui.Grid item xs={12} sm={6} md={4} lg={3}>
              <Mui.Autocomplete
                options={locationOptions}
                value={form.watch("location") || null}
                onChange={(_event, value) => form.setValue("location", value)}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <Mui.TextField
                    {...params}
                    label={t("parkingTool.filters.location", "Location")}
                  />
                )}
                renderOption={(props, option, state) => {
                  return (
                    <li {...props} key={props.id}>
                      {option.name}
                    </li>
                  );
                }}
              />
            </Mui.Grid>
            
            <Mui.Grid item xs={12} sm={6} md={4} lg={3}>
              <Mui.Box pl={2}>
                <Mui.FormControlLabel
                  control={ <Mui.Switch /> }
                  label={
                    <Mui.Box pl={2}>
                      {form.watch("today") ? t("parkingTool.filters.today") : t("parkingTool.filters.tomorrow")}
                    </Mui.Box>
                  }
                  onChange={(_event, value) => form.setValue("today", value)}
                  checked={form.watch("today") || false}
                />
              </Mui.Box>
            </Mui.Grid>

            <Mui.Grid item xs={12} sm={6} md={4} lg={3} display="flex" justifyContent="end" gap={2}>
              <Mui.Grid container spacing={2.5} justifyContent="end">
                <Mui.Grid item xs={4} />
                <Mui.Grid item xs={4} display="flex" justifyContent="end">
                  <Mui.Button
                    variant="text"
                    size="icon"
                    color="accent"
                    onClick={onReset}
                    startIcon={<MuiIcons.Replay />}
                  >
                    {t("parkingTool.filters.reset")}
                  </Mui.Button>
                </Mui.Grid>

                <Mui.Grid item xs={4} display="flex" justifyContent="end">
                  <Mui.Button
                    type="submit"
                    variant="contained"
                    size="icon"
                  >
                    <MuiIcons.Search />
                  </Mui.Button>
                </Mui.Grid>
              </Mui.Grid>
            </Mui.Grid>

          </Mui.Grid>
        </Mui.CardContent>
      </Mui.Card>
      
      <CustomLoader loading={loading} />
    </form>
  )
}

export default ParkingToolFilter;